import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import { usePermissions } from '@/hooks/usePermissions';
import ApplyProposalTemplateModal from '@/ui/pages/proposalBuilderPage/components/ApplyProposalTemplateModal';
import EditReferenceModal from '@/ui/pages/proposalBuilderPage/components/EditReferenceModal';
import { useDownloadProposalPdf } from '@/ui/pages/proposalBuilderPage/components/useDownloadProposalPdf';
import { useDuplicateProposal } from '@/ui/pages/proposalBuilderPage/components/useDuplicateProposal';
import { useHashFragment } from '@/utils/customHooks';
import { ContextMenuDropdown, TDropdownItem } from '@payaca/ambrosia';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';
import { JobStatus } from '@payaca/types/jobTypes';
import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useDispatch } from 'react-redux';

const APPLY_TEMPLATE_MODAL_HASH = '#apply-template';
const EDIT_REFERENCE_MODAL_HASH = '#edit-reference';

type ProposalActionsMenuProps = {
  /** Public id of the Proposal being edited (the route param). */
  proposalId: string;
  projectId: string;
  /**
   * Called once an applied Template's fresh content has been refetched. Omit
   * where there is no content editor to refresh — Apply Template is then not
   * offered.
   */
  onTemplateApplied?: () => void;
};

/**
 * The Draft Proposal's actions menu, in the builder header. A sent Proposal
 * lists its own actions in the panel instead, so nothing here has to survive a
 * send.
 */
const ProposalActionsMenu: FC<ProposalActionsMenuProps> = (props) => {
  const { proposalId, projectId, onTemplateApplied } = props;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { proposal } = useGetProposal(proposalId);
  const { userHasRequiredPermission } = usePermissions();

  const [isApplyTemplateModalOpen, toggleApplyTemplateModal] = useHashFragment(
    APPLY_TEMPLATE_MODAL_HASH
  );
  const [isEditReferenceModalOpen, toggleEditReferenceModal] = useHashFragment(
    EDIT_REFERENCE_MODAL_HASH
  );
  // A Proposal shares the legacy `jobs` row, so `proposal.id` is the numeric
  // job id that the legacy job-update API expects.
  const numericJobId = proposal?.id ? Number(proposal.id) : undefined;
  const { downloadPdf, isDownloading, canDownload } =
    useDownloadProposalPdf(proposalId);
  const duplicate = useDuplicateProposal({ proposalId, projectId });
  const canUseProposalTemplates = userHasRequiredPermission([
    TemplatesPermissions.USE_PROPOSAL_TEMPLATES,
  ]);
  const canEditReference = userHasRequiredPermission([
    JobPermissions.UPDATE_JOB_REFERENCE,
  ]);
  const duplicateAvailability = proposal?.actionAvailability.duplicate;

  // A Proposal is presented as either a Quote or an Estimate. The switch action
  // flips it to the other type, offering only the type it is not currently.
  let switchTarget: { label: string; status: JobStatus } | undefined;
  if (proposal?.documentType === 'QUOTE') {
    switchTarget = {
      label: 'Switch to estimate',
      status: JobStatus.NEW_ESTIMATE,
    };
  } else if (proposal?.documentType === 'ESTIMATE') {
    switchTarget = { label: 'Switch to quote', status: JobStatus.NEW_QUOTE };
  }

  const handleSwitchType = (newStatus: JobStatus) => {
    if (!numericJobId) return;

    dispatch(
      jobsActions.partialUpdateJob.request({
        jobId: numericJobId,
        data: { status: newStatus },
        callback: () => {
          dispatch(jobsActions.requestGetJob(numericJobId));
          void queryClient.invalidateQueries({
            queryKey: proposalKeys.proposal(proposalId),
          });
          void queryClient.invalidateQueries({
            queryKey: projectKeys.project(projectId),
          });
        },
      })
    );
  };

  const menuItems: TDropdownItem[] = [
    ...(canEditReference
      ? [
          {
            label: 'Edit reference',
            onClick: toggleEditReferenceModal,
          },
        ]
      : []),
    // Hidden without the permission, as legacy hid it; blocked by state, it
    // explains itself.
    ...(duplicate.canDuplicate
      ? [
          {
            label: 'Duplicate',
            onClick: duplicate.openDuplicate,
            disabled: duplicateAvailability
              ? !duplicateAvailability.result
              : false,
            // MenuItemCore renders reasons only when disabled.
            disabledReasons: duplicateAvailability?.reasons,
          },
        ]
      : []),
    {
      label: 'Download PDF',
      disabled: !canDownload || isDownloading,
      onClick: downloadPdf,
    },
    ...(switchTarget
      ? [
          {
            label: switchTarget.label,
            onClick: () => handleSwitchType(switchTarget.status),
          },
        ]
      : []),
    ...(canUseProposalTemplates && onTemplateApplied
      ? [
          {
            label: 'Apply template',
            onClick: toggleApplyTemplateModal,
          },
        ]
      : []),
  ];

  return (
    <>
      <ContextMenuDropdown
        size="sm"
        variant="secondary"
        label="Proposal actions"
        isProcessing={isDownloading}
        menuItems={menuItems}
      />

      {onTemplateApplied && (
        <ApplyProposalTemplateModal
          isOpen={isApplyTemplateModalOpen}
          onClose={toggleApplyTemplateModal}
          proposalId={proposalId}
          projectId={projectId}
          onTemplateApplied={onTemplateApplied}
        />
      )}

      <EditReferenceModal
        isOpen={isEditReferenceModalOpen}
        onClose={toggleEditReferenceModal}
        proposalId={proposalId}
      />

      {duplicate.modal}
    </>
  );
};

export default ProposalActionsMenu;
